import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { CartItem } from '../Types/CartTypes';

type CartContextValue = {
  items: CartItem[];
  totalItems: number;
  totalAmount: number;
  addItem: (item: CartItem) => void;
  replacePayableItem: (item: CartItem) => void;
  removeItem: (id: string) => void;
  clearCart: () => void;
};

const STORAGE_KEY = 'sandras-portal-cart-v1';

const CartContext = createContext<CartContextValue | undefined>(undefined);

const normalizeCart = (items: CartItem[]): CartItem[] => {
  const bookings = items.filter(item => item.type === 'booking');
  const payableItems = items.filter(item => item.type !== 'booking');

  if (payableItems.length <= 1) {
    return [...bookings, ...payableItems];
  }

  return [...bookings, payableItems[payableItems.length - 1]];
};

const loadCart = (): CartItem[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CartItem[];
    return Array.isArray(parsed) ? normalizeCart(parsed) : [];
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>(() => loadCart());

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (item: CartItem) => {
    setItems(prev => {
      if (item.type !== 'booking') {
        return normalizeCart([...prev.filter(x => x.type === 'booking'), item]);
      }

      const updated = prev.filter(x => !(x.type === item.type && x.referenceId === item.referenceId));
      return normalizeCart([...updated, item]);
    });
  };

  const replacePayableItem = (item: CartItem) => {
    setItems(prev => {
      const nonPayableItems = prev.filter(x => x.type === 'booking');
      return normalizeCart([...nonPayableItems, item]);
    });
  };

  const removeItem = (id: string) => {
    setItems(prev => prev.filter(item => item.id !== id));
  };

  const clearCart = () => setItems([]);

  const totalAmount = useMemo(
    () => items.reduce((sum, item) => sum + (item.price * item.quantity), 0),
    [items]
  );

  const totalItems = useMemo(
    () => items.reduce((sum, item) => sum + item.quantity, 0),
    [items]
  );

  return (
    <CartContext.Provider value={{ items, totalItems, totalAmount, addItem, replacePayableItem, removeItem, clearCart }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside CartProvider');
  return ctx;
};
